import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Avatar,
    Stack,
    Divider,
    Button,
    Grid,
    Skeleton,
    Chip,
    Paper,
    IconButton,
    Breadcrumbs,
    Link,
    Dialog,
    CircularProgress,
    Snackbar,
    Alert,
    Switch,
    FormControlLabel,
    TextField,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { ArrowBack, Edit, Delete, Inventory, CalendarToday, Category, Close, Save } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useProductDetails, useDeleteProduct, useUpdateProduct } from 'hooks/useProduct';
import ConfirmDeleteDialog from 'ui-component/ConfirmDeleteDialog';

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    // API Hooks
    const { data: product, isLoading, error } = useProductDetails(id);
    const { mutate: deleteProduct, isPending: isDeleting } = useDeleteProduct();
    const { mutate: updateProduct, isPending: isUpdating } = useUpdateProduct();

    // UI States
    const [openLightbox, setOpenLightbox] = useState(false);
    const [selectedImg, setSelectedImg] = useState('');
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Quick Update States
    const [stockDialogOpen, setStockDialogOpen] = useState(false);
    const [newStock, setNewStock] = useState(0);

    // Sync local state when product data loads/changes
    useEffect(() => {
        if (product) {
            setNewStock(product.stock);
        }
    }, [product]);

    // --- Handlers ---

    const handleToggleAvailability = () => {
        // We pass the inverse of current state
        const updatedStatus = !product.available;

        updateProduct(
            { id, payload: { available: updatedStatus } },
            {
                onSuccess: () => {
                    setSnackbar({
                        open: true,
                        message: `Product is now ${updatedStatus ? 'Visible' : 'Hidden'}`,
                        severity: 'success'
                    });
                },
                onError: (err) => {
                    setSnackbar({
                        open: true,
                        message: err?.response?.data?.message || 'Update failed',
                        severity: 'error'
                    });
                }
            }
        );
    };

    const handleUpdateStock = () => {
        const stockValue = Number(newStock);
        if (isNaN(stockValue) || stockValue < 0) {
            setSnackbar({ open: true, message: 'Please enter a valid stock number', severity: 'warning' });
            return;
        }

        updateProduct(
            { id, payload: { stock: stockValue } },
            {
                onSuccess: () => {
                    setSnackbar({ open: true, message: 'Stock level updated!', severity: 'success' });
                    setStockDialogOpen(false);
                },
                onError: (err) => {
                    setSnackbar({
                        open: true,
                        message: err?.response?.data?.message || 'Update failed',
                        severity: 'error'
                    });
                }
            }
        );
    };

    const handleDelete = () => {
        deleteProduct(id, {
            onSuccess: () => {
                setConfirmDeleteOpen(false);
                navigate('/vendor/products', {
                    state: {
                        snackbar: { message: 'Product deleted successfully!', severity: 'success' }
                    }
                });
            },
            onError: (err) => {
                setConfirmDeleteOpen(false);
                setSnackbar({ open: true, message: err?.message || 'Delete failed', severity: 'error' });
            }
        });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    if (isLoading) return <LoadingSkeleton />;
    if (error || !product) return <ErrorState message="Product not found" />;

    return (
        <Box p={{ xs: 2, md: 4 }} maxWidth="lg" margin="auto">
            {/* Header: Navigation & Actions */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={4}>
                <Box>
                    <IconButton onClick={() => navigate(-1)} sx={{ mb: 1 }}>
                        <ArrowBack />
                    </IconButton>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                            Dashboard
                        </Link>
                        <Link underline="hover" color="inherit" onClick={() => navigate('/vendor/products')} sx={{ cursor: 'pointer' }}>
                            Products
                        </Link>
                        <Typography color="text.primary">Details</Typography>
                    </Breadcrumbs>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                    <Button startIcon={<Edit />} variant="outlined" onClick={() => navigate(`/vendor/products/${id}/edit`)}>
                        Edit Details
                    </Button>
                    <Button
                        startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <Delete />}
                        variant="contained"
                        color="error"
                        onClick={() => setConfirmDeleteOpen(true)}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Stack>
            </Stack>

            <Grid container spacing={4}>
                {/* Left: Image Gallery */}
                <Grid item xs={12} md={5}>
                    <Paper
                        elevation={0}
                        onClick={() => {
                            setSelectedImg(product.thumbnail);
                            setOpenLightbox(true);
                        }}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 4,
                            overflow: 'hidden',
                            cursor: 'zoom-in',
                            position: 'relative'
                        }}
                    >
                        <Avatar src={product.thumbnail} variant="square" sx={{ width: '100%', height: 'auto', aspectRatio: '1/1' }} />
                        {!product.available && (
                            <Box sx={{ position: 'absolute', top: 15, right: 15 }}>
                                <Chip label="HIDDEN / DRAFT" color="error" size="small" sx={{ fontWeight: 'bold' }} />
                            </Box>
                        )}
                    </Paper>

                    {product.images?.length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
                                Gallery Images ({product.images.length})
                            </Typography>
                            <Grid container spacing={1}>
                                {product.images.map((img, index) => (
                                    <Grid item key={index} xs={3}>
                                        <Avatar
                                            src={img}
                                            variant="rounded"
                                            onClick={() => {
                                                setSelectedImg(img);
                                                setOpenLightbox(true);
                                            }}
                                            sx={{
                                                width: '100%',
                                                height: 70,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                cursor: 'zoom-in',
                                                '&:hover': { borderColor: 'primary.main' }
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Grid>

                {/* Right: Management & Info */}
                <Grid item xs={12} md={7}>
                    {/* Quick Management Card */}
                    <Paper variant="outlined" sx={{ p: 2.5, mb: 4, borderRadius: 3, bgcolor: 'background.default', borderStyle: 'dashed' }}>
                        <Typography variant="overline" display="block" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                            Quick Inventory Actions
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={product.available}
                                        onChange={handleToggleAvailability}
                                        disabled={isUpdating}
                                        color="success"
                                    />
                                }
                                label={product.available ? 'Currently Live' : 'Hidden from Shop'}
                            />
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<Inventory />}
                                onClick={() => setStockDialogOpen(true)}
                                disabled={isUpdating}
                            >
                                Update Stock ({product.stock})
                            </Button>
                        </Stack>
                    </Paper>

                    <Box>
                        <Typography variant="h3" fontWeight={800} gutterBottom>
                            {product.name}
                        </Typography>

                        <Typography variant="h4" color="primary.main" fontWeight={700} sx={{ mb: 3 }}>
                            ₦{Number(product.price).toLocaleString()}
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Grid container spacing={3}>
                            <InfoItem
                                icon={<Category color="primary" />}
                                label="Category"
                                value={product.category?.name || 'Uncategorized'}
                            />
                            <InfoItem
                                icon={<Inventory color={product.stock < 5 ? 'error' : 'primary'} />}
                                label="Available Stock"
                                value={`${product.stock} units`}
                            />
                            <InfoItem
                                icon={<CalendarToday color="primary" />}
                                label="Date Created"
                                value={new Date(product.createdAt).toLocaleDateString(undefined, { dateStyle: 'long' })}
                            />
                        </Grid>

                        <Typography variant="h6" sx={{ mt: 5, mb: 1, fontWeight: 700 }}>
                            Product Description
                        </Typography>
                        <Typography color="text.secondary" lineHeight={1.8} sx={{ whiteSpace: 'pre-line' }}>
                            {product.description || 'No description provided.'}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* --- Dialogs --- */}

            <Dialog open={stockDialogOpen} onClose={() => !isUpdating && setStockDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Adjust Stock Quantity</DialogTitle>
                <DialogContent>
                    <Box pt={1}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Total Units in Stock"
                            value={newStock}
                            onChange={(e) => setNewStock(e.target.value)}
                            disabled={isUpdating}
                            helperText="Set the current physical quantity available for sale."
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setStockDialogOpen(false)} color="inherit" disabled={isUpdating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdateStock}
                        variant="contained"
                        disabled={isUpdating}
                        startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <Save />}
                    >
                        Save Quantity
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openLightbox} onClose={() => setOpenLightbox(false)} maxWidth="md">
                <Box position="relative" bgcolor="black" p={1}>
                    <IconButton
                        onClick={() => setOpenLightbox(false)}
                        sx={{ position: 'absolute', top: 10, right: 10, color: 'white', zIndex: 1 }}
                    >
                        <Close />
                    </IconButton>
                    <img src={selectedImg} alt="Preview" style={{ width: '100%', display: 'block' }} />
                </Box>
            </Dialog>

            <ConfirmDeleteDialog
                open={confirmDeleteOpen}
                loading={isDeleting}
                title="Delete Product?"
                subtitle={`Are you sure you want to delete "${product.name}"? This action is permanent.`}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={handleDelete}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

// --- Helper Components ---

function InfoItem({ icon, label, value }) {
    return (
        <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'action.hover', display: 'flex' }}>{icon}</Box>
                <Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        display="block"
                        sx={{ textTransform: 'uppercase' }}
                    >
                        {label}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                        {value}
                    </Typography>
                </Box>
            </Stack>
        </Grid>
    );
}

InfoItem.propTypes = {
    icon: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

function LoadingSkeleton() {
    return (
        <Box p={4} maxWidth="lg" margin="auto">
            <Skeleton variant="text" width={150} sx={{ mb: 4 }} />
            <Grid container spacing={4}>
                <Grid item xs={12} md={5}>
                    <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 4 }} />
                </Grid>
                <Grid item xs={12} md={7}>
                    <Skeleton variant="text" width="60%" height={60} />
                    <Skeleton variant="text" width="40%" height={40} />
                    <Skeleton variant="rectangular" width="100%" height={150} sx={{ mt: 4 }} />
                </Grid>
            </Grid>
        </Box>
    );
}

function ErrorState({ message }) {
    return (
        <Box p={4} textAlign="center">
            <Typography color="error" variant="h6">
                {message}
            </Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
                Retry
            </Button>
        </Box>
    );
}

ErrorState.propTypes = {
    message: PropTypes.string.isRequired
};
